import { getPermissionForHref, getPermissionForPath } from './routePermissions';

const PERMISSION_ALIASES = {
  can_manage_protocols: ['protocols.view', 'protocols.create', 'protocols.edit', 'protocols.delete'],
  can_manage_inventory: [
    'feed_inventory.view', 'feed_inventory.create', 'feed_inventory.edit', 'feed_inventory.delete',
    'feed_operations.view', 'feed_operations.create', 'feed_operations.edit', 'feed_operations.delete',
    'supply_inventory.view', 'supply_inventory.create', 'supply_inventory.edit', 'supply_inventory.delete',
  ],
  can_manage_procurement: [
    'procurement.view', 'procurement.create', 'procurement.edit', 'procurement.delete',
  ],
  can_manage_inventory_master_data: [
    'feed_master_data.view', 'feed_master_data.create', 'feed_master_data.edit', 'feed_master_data.delete',
  ],
  can_manage_inventory_documents: [
    'inventory_documents.view', 'inventory_documents.create', 'inventory_documents.edit', 'inventory_documents.delete',
  ],
  can_post_inventory_documents: ['inventory_documents.post'],
  can_reverse_inventory_documents: ['inventory_documents.reverse'],
  can_import_inventory_documents: ['inventory_documents.import'],
  can_view_inventory_reports: ['inventory_reports.view'],
  can_register_service: ['services.view', 'services.create', 'services.edit', 'services.delete'],
  can_view_audit_logs: ['audit_logs.view'],
  can_manage_financial_data: [
    'financial_data.view', 'financial_data.create', 'financial_data.edit', 'financial_data.delete',
    'milk_sales.view', 'milk_sales.create', 'milk_sales.edit', 'milk_sales.delete',
    'counterparties.view', 'counterparties.create', 'counterparties.edit', 'counterparties.delete',
    'livestock_sales.view', 'livestock_sales.create', 'livestock_sales.edit', 'livestock_sales.delete',
  ],
  can_view_management_reports: ['management_reports.view', 'reports.view'],
  can_create_treatment: [
    'treatments.view', 'treatments.create',
    'calf_treatment_sheets.view', 'calf_treatment_sheets.create',
    'cow_treatment_sheets.view', 'cow_treatment_sheets.create',
  ],
  can_execute_injection: ['daily_plan.execute', 'treatments.edit', 'calf_treatment_sheets.edit', 'calf_treatment_sheets.recover', 'cow_treatment_sheets.edit'],
};

const DEFAULT_VIEW_PERMISSIONS = [
  'dashboard.view',
  'operations.view',
  'alerts.view',
  'cows.view',
  'baharbands.view',
  'cow_documents.view',
  'livestock_exit_events.view',
  'calves.view',
  'calf_events.view',
  'calf_daily_tasks.view',
  'calf_treatment_sheets.view',
  'calf_treatment_protocols.view',
  'cow_treatment_protocols.view',
  'calf_reports.view',
  'calf_milk_records.view',
  'calf_milk_protocols.view',
  'reproduction.view',
  'estrus.view',
  'vet_visit.view',
  'services.view',
  'pregnancy_checks.view',
  'abortions.view',
  'dryoffs.view',
  'calvings.view',
  'repro_reports.view',
  'semen_inventory.view',
  'health.view',
  'health_workbench.view',
  'treatments.view',
  'disease_master_data.view',
  'preventive_care.view',
  'udder_health.view',
  'mastitis_cases.view',
  'mastitis_followups.view',
  'lameness_events.view',
  'lameness_followups.view',
  'health_history_export.view',
  'cow_treatment_history.view',
  'vaccination_protocols.view',
  'health_reports.view',
  'milk_records.view',
  'milk_reports.view',
  'milk_sales.view',
  'counterparties.view',
  'reports.view',
  'settings.view',
  'power_user.view',
  'drugs.view',
  'feed_inventory.view',
  'feed_master_data.view',
  'feed_operations.view',
  'inventory_reports.view',
];

const ROLE_PERMISSION_FALLBACKS = {
  viewer: DEFAULT_VIEW_PERMISSIONS,
  operator: [
    ...DEFAULT_VIEW_PERMISSIONS,
    'daily_plan.view',
    'daily_plan.execute',
  ],
  inseminator: [
    ...DEFAULT_VIEW_PERMISSIONS,
    'estrus.create',
    'services.create',
    'services.edit',
  ],
  vet: [
    ...DEFAULT_VIEW_PERMISSIONS,
    'daily_plan.view',
    'daily_plan.execute',
    'estrus.create',
    'pregnancy_checks.create',
    'abortions.create',
    'dryoffs.create',
    'calvings.create',
    'cow_treatment_protocols.create',
    'cow_treatment_protocols.edit',
    'udder_health.view', 'udder_health.create', 'udder_health.edit',
    'mastitis_cases.view', 'mastitis_cases.create', 'mastitis_cases.edit',
    'mastitis_followups.view', 'mastitis_followups.create', 'mastitis_followups.edit',
    'lameness_events.view', 'lameness_events.create', 'lameness_events.edit',
    'lameness_followups.view', 'lameness_followups.create', 'lameness_followups.edit',
    'health_history_export.view',
  ],
  repro_manager: [
    ...DEFAULT_VIEW_PERMISSIONS,
    'daily_plan.view',
    'daily_plan.execute',
    'estrus.create',
    'pregnancy_checks.create',
    'abortions.create',
    'dryoffs.create',
    'calvings.create',
    'protocols.view',
    'protocols.create',
    'protocols.edit',
    'protocols.delete',
    'udder_health.view', 'udder_health.create', 'udder_health.edit',
    'mastitis_cases.view', 'mastitis_cases.create', 'mastitis_cases.edit',
    'mastitis_followups.view', 'mastitis_followups.create', 'mastitis_followups.edit',
    'lameness_events.view', 'lameness_events.create', 'lameness_events.edit',
    'lameness_followups.view', 'lameness_followups.create', 'lameness_followups.edit',
    'health_history_export.view',
    'cow_treatment_protocols.create',
    'cow_treatment_protocols.edit',
  ],
  inventory_manager: [
    ...DEFAULT_VIEW_PERMISSIONS,
    'feed_inventory.view',
    'supply_inventory.view',
    'procurement.view',
    'feed_master_data.view',
    'feed_operations.view',
    'feed_operations.create',
    'feed_operations.edit',
    'inventory_documents.view',
    'inventory_reports.view',
  ],
};

const DIRECT_PERMISSION_ALIASES = Object.entries(PERMISSION_ALIASES).reduce((acc, [alias, permissions]) => {
  permissions.forEach((permission) => {
    if (!acc[permission]) acc[permission] = [];
    acc[permission].push(alias);
  });
  return acc;
}, {});

function normalizePermissionList(permission) {
  if (!permission) return [];
  if (Array.isArray(permission)) {
    return permission.flatMap((item) => normalizePermissionList(item));
  }
  const key = String(permission).trim();
  if (!key) return [];
  return [key, ...(PERMISSION_ALIASES[key] || [])];
}

function getExplicitEffectivePermissionSet(user) {
  if (!Array.isArray(user?.effective_permissions)) return null;
  return new Set(user.effective_permissions);
}

function getRoleFallbackPermissions(user) {
  if (!user?.role) return [];
  if (getExplicitEffectivePermissionSet(user)) return [];
  return ROLE_PERMISSION_FALLBACKS[user.role] || [];
}

function hasDirectOrAliasedPermission(user, candidate) {
  if (!candidate) return false;

  const direct = !!user?.permissions?.[candidate];
  if (direct) return true;

  const aliases = DIRECT_PERMISSION_ALIASES[candidate] || [];
  if (aliases.some((alias) => !!user?.permissions?.[alias])) return true;

  const explicitPermissions = getExplicitEffectivePermissionSet(user);
  if (explicitPermissions) {
    if (explicitPermissions.has(candidate)) return true;
    return aliases.some((alias) => explicitPermissions.has(alias));
  }

  const fallback = getRoleFallbackPermissions(user);
  return fallback.includes(candidate);
}

export function canUserAccess(user, permission, { adminOnly = false, requireAll = false } = {}) {
  if (!user) return false;
  const isAdmin = !!user?.is_admin || user?.role === 'admin';
  if (adminOnly) return isAdmin;
  if (!permission) return true;

  const candidates = Array.from(new Set(normalizePermissionList(permission)));
  if (!candidates.length) return true;
  if (isAdmin) return true;

  const checks = candidates.map((candidate) => hasDirectOrAliasedPermission(user, candidate));
  return requireAll ? checks.every(Boolean) : checks.some(Boolean);
}

export function hasAnyPermission(user, permissions = []) {
  return canUserAccess(user, permissions, { requireAll: false });
}

export function hasAllPermissions(user, permissions = []) {
  return canUserAccess(user, permissions, { requireAll: true });
}

export function buildCrudPermission(resource, action = 'view') {
  if (!resource) return null;
  return `${resource}.${action}`;
}

export function getCrudPermissions(user, resource) {
  if (!resource) {
    return { view: true, create: true, edit: true, delete: true, manage: true };
  }
  const perms = {
    view: canUserAccess(user, buildCrudPermission(resource, 'view')),
    create: canUserAccess(user, buildCrudPermission(resource, 'create')),
    edit: canUserAccess(user, buildCrudPermission(resource, 'edit')),
    delete: canUserAccess(user, buildCrudPermission(resource, 'delete')),
  };
  return { ...perms, manage: perms.create || perms.edit || perms.delete };
}

export function canAccessItem(user, item = {}) {
  if (!item) return false;
  const inferredPermission = item.permission || item.permissions || getPermissionForHref(item.href);
  if (!canUserAccess(user, inferredPermission || null, { adminOnly: item.adminOnly, requireAll: !!item.requireAllPermissions })) {
    return false;
  }
  if (typeof item.allowed === 'function') {
    return !!item.allowed(user);
  }
  return true;
}

export function canAccessRoute(user, pathname = '') {
  const permission = getPermissionForPath(pathname);
  return canUserAccess(user, permission || null);
}

export function filterAccessibleItems(items = [], user) {
  return (items || []).filter((item) => canAccessItem(user, item));
}

export { DEFAULT_VIEW_PERMISSIONS, DIRECT_PERMISSION_ALIASES, PERMISSION_ALIASES, ROLE_PERMISSION_FALLBACKS };
